import { charMaps, flipText, mirrorText } from './unicode';

// Leave whole non-ASCII graphemes untouched; do not transliterate names or split emoji.
function graphemes(text) {
  return Array.from(new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text), part => part.segment);
}
// Five rows of three columns; lowercase uses the same uppercase block glyph.
const BLOCKS = {
  A: '010101111101101', B: '110101110101110', C: '011100100100011', D: '110101101101110',
  E: '111100110100111', F: '111100110100100', G: '011100101101011', H: '101101111101101',
  I: '111010010010111', J: '001001001101010', K: '101101110101101', L: '100100100100111',
  M: '101111111101101', N: '101111111111101', O: '010101101101010', P: '110101110100100',
  Q: '010101101111011', R: '110101110101101', S: '011100010001110', T: '111010010010010',
  U: '101101101101111', V: '101101101101010', W: '101101111111101', X: '101101010101101',
  Y: '101101010010010', Z: '111001010100111',
  0: '111101101101111', 1: '010110010010111', 2: '110001010100111', 3: '110001010001110',
  4: '101101111001001', 5: '111100110001110', 6: '011100111101111', 7: '111001010010010',
  8: '111101111101111', 9: '111101111001110', ' ': '000000000000000',
};
function asciiText(text) {
  if (!text) return '';
  const newline = (text.match(/\r\n|\r|\n/) || ['\n'])[0];
  return text.split(/(\r\n|\r|\n)/).map((line, index) => {
    if (index % 2) return line;
    const letters = graphemes(line);
    return Array.from({ length: 5 }, (_, row) => letters.map(letter => {
      const pattern = /^[a-z0-9 ]$/i.test(letter) ? BLOCKS[letter.toUpperCase()] : null;
      return pattern ? pattern.slice(row * 3, row * 3 + 3).replace(/1/g, '#').replace(/0/g, ' ')
        : row === 2 ? letter : ' ';
    }).join(' ')).join(newline);
  }).join('');
}
export const INPUT_LIMIT = 500;
export const BIG_INPUT_LIMIT = 80;
export const CODE_UNIT_LIMIT = 8000;
export function limitInput(text, limit = INPUT_LIMIT) {
  let result = '';
  let count = 0;
  // Stop segmentation early on a large paste; never materialize the unbounded input.
  const segments = new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text);
  for (const { segment: part } of segments) {
    if (count >= limit || result.length + part.length > CODE_UNIT_LIMIT) break;
    result += part;
    count += 1;
  }
  return result;
}
export function transformText(text, style) {
  const limit = style === 'ascii' ? BIG_INPUT_LIMIT : INPUT_LIMIT;
  if (text.length > CODE_UNIT_LIMIT) throw new RangeError('Maximum 8000 code units.');
  if (graphemes(text).length > limit) throw new RangeError(`Maximum ${limit} characters.`);
  if (style === 'ascii') return asciiText(text);
  if (style === 'strike') return graphemes(text).map(part =>
    /^[\p{L}\p{N}\p{P}][\p{M}]*$/u.test(part) && !/[\u0336\u20e3\ufe0f]/u.test(part) ? part + '\u0336' : part).join('');
  if (['reverse', 'upsideDown', 'mirror'].includes(style)) {
    const map = style === 'upsideDown' ? flipText : style === 'mirror' ? mirrorText : part => part;
    return text.split(/(\r\n|\r|\n)/).map((line, index) => index % 2 ? line :
      graphemes(line).reverse().map(part => /^[\x20-\x7e]$/.test(part) ? map(part) : part).join('')).join('');
  }
  const map = charMaps[style];
  return graphemes(text).map(part => /^[\x20-\x7e]$/.test(part) && map ? map(part) : part).join('');
}
